import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { WorkUnitRow } from "./workunits";

export interface VisibilityRow {
  id: string;
  clientId: string;
  clientName: string | null;
  departmentId: string;
  departmentName: string | null;
  status: "active" | "inactive";
  grantedById: string | null;
  grantedByName: string | null;
  grantedAt: string | null;
  revokedById: string | null;
  revokedByName: string | null;
  revokedAt: string | null;
  createdAt: string | null;
}

export interface ClientDocumentRow {
  id: string;
  clientId: string;
  clientName: string | null;
  groupId: string | null;
  groupName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  description: string;
  status: "active" | "inactive";
  lifecycle: "linked" | "unlinked" | "other";
  attachmentId: string | null;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  docNo: string;
  uploadedById: string | null;
  uploadedByName: string | null;
  uploadedAt: string | null;
  linkedWorkUnitId: string | null;
  linkedWorkUnitCtrlNo: string | null;
  linkedById: string | null;
  linkedByName: string | null;
  linkedAt: string | null;
  financialYear: string;
  linkedWorkflowDeptName: string | null;
  linkedStepsCompleted: number;
  linkedTotalSteps: number;
  linkedProgressPct: number;
  ackEmailStatus: "" | "sent" | "failed" | "skipped" | string;
  ackEmailTo: string;
  ackEmailSentAt: string | null;
  ackEmailError: string;
  origin?: "client_upload" | "approval_publish";
  sourceStepCompletionId?: string | null;
}

interface Paginated<T> {
  results: T[];
  nextCursor: string | null;
  prevCursor: string | null;
}

// ---------- Visibility (admin side) ----------

export function useClientVisibility(clientId: string | null) {
  return useQuery({
    queryKey: ["client-visibility", clientId],
    enabled: !!clientId,
    queryFn: () =>
      api
        .get<Paginated<VisibilityRow>>(`/clients/${clientId}/visibility`)
        .then((r) => r.data.results),
  });
}

export function useGrantClientVisibility(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (departmentId: string) =>
      api
        .post<VisibilityRow>(`/clients/${clientId}/visibility`, { departmentId })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-visibility", clientId] });
    },
  });
}

export function useRevokeClientVisibility(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (visibilityId: string) =>
      api
        .post<VisibilityRow>(`/client-visibility/${visibilityId}/revoke`, {})
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-visibility", clientId] });
    },
  });
}

// ---------- Client portal (client role) ----------

export interface ClientPortalSelfUser {
  id: string;
  fullName: string;
  loginEmail: string;
  communicationEmail: string;
  ackEmail: string;
  ackEmailIsLoginFallback: boolean;
  needsCommunicationEmail: boolean;
}

export interface ClientPortalDept {
  departmentId: string;
  departmentName: string;
}

export interface ClientGroupMember {
  clientId: string;
  clientName: string;
  isSelf: boolean;
  contactEmail: string;
  visibleDepartments: ClientPortalDept[];
  docCount: number;
}

export interface ClientPortalSelfPayload {
  client: {
    id: string;
    name: string;
    groupId: string | null;
    groupName: string | null;
  };
  visibleDepartments: ClientPortalDept[];
  groupMembers: ClientGroupMember[];
  user: ClientPortalSelfUser;
}

export function useClientPortalSelf() {
  return useQuery({
    queryKey: ["client-portal-me"],
    queryFn: () =>
      api.get<ClientPortalSelfPayload>("/client-portal/me").then((r) => r.data),
  });
}

export function useUpdateClientCommunicationEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (communicationEmail: string) =>
      api
        .patch<ClientPortalSelfPayload>("/client-portal/me", {
          communicationEmail,
        })
        .then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(["client-portal-me"], data);
    },
  });
}

export function useUploadClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      file: File;
      departmentId: string | null;
      description: string;
      clientId?: string | null;
    }) => {
      const fd = new FormData();
      fd.append("file", input.file);
      if (input.clientId) fd.append("clientId", input.clientId);
      if (input.departmentId) fd.append("departmentId", input.departmentId);
      if (input.description) fd.append("description", input.description);
      return api
        .post<ClientDocumentRow>("/client-portal/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-portal-my-docs"] });
    },
  });
}

export function useMyClientDocs(memberClientId?: string | null) {
  return useQuery({
    queryKey: ["client-portal-my-docs", memberClientId ?? null],
    queryFn: () =>
      api
        .get<Paginated<ClientDocumentRow>>("/client-portal/documents", {
          params: { memberClientId: memberClientId || undefined },
        })
        .then((r) => r.data.results),
  });
}

// ---------- Admin queue ----------

export interface ClientDocFilters {
  clientId?: string;
  departmentId?: string;
  lifecycle?: "linked" | "unlinked" | "other";
  status?: "active" | "inactive";
  workUnitId?: string;
  search?: string;
}

export interface ClientDocsCounts {
  total: number;
  unrouted: number;
  unlinked: number;
  linked: number;
  actionable: number;
}

export function useClientDocsCounts(enabled = true) {
  return useQuery<ClientDocsCounts>({
    queryKey: ["client-docs", "counts"],
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
    queryFn: () =>
      api.get<ClientDocsCounts>("/client-docs/counts").then((r) => r.data),
  });
}

export function useClientDocs(filters: ClientDocFilters = {}) {
  return useQuery({
    queryKey: ["client-docs", filters],
    queryFn: () =>
      api
        .get<Paginated<ClientDocumentRow>>("/client-docs", { params: filters })
        .then((r) => r.data.results),
  });
}

export function useClientDoc(docId: string | null) {
  return useQuery({
    queryKey: ["client-doc", docId],
    enabled: !!docId,
    queryFn: () =>
      api.get<ClientDocumentRow>(`/client-docs/${docId}`).then((r) => r.data),
  });
}

function invalidateAllClientDocLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["client-docs"] });
  qc.invalidateQueries({ queryKey: ["client-portal-my-docs"] });
  qc.invalidateQueries({ queryKey: ["work-unit-client-docs"] });
}

export function useSetClientDocDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docId: string; departmentId: string }) =>
      api
        .post<ClientDocumentRow>(
          `/client-docs/${input.docId}/set-department`,
          { departmentId: input.departmentId },
        )
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

/**
 * Work units a document may be linked to — spans every client in the doc's
 * client ClientGroup (or just the doc's client when it has no group). Server
 * derives group + department from the doc itself, so the returned set is
 * exactly what `useLinkClientDoc` will accept. `search` is server-side; debounce
 * at the call site for large groups (ADR-033).
 */
export interface LinkableWorkUnitFilters {
  /** "active" (dialog default) | "completed" | "all" */
  lifecycle?: "active" | "completed" | "all";
  /** Narrow to one client of the doc's group family. */
  clientId?: string | null;
  /** ctrl_no (default) | -ctrl_no | created_at | -created_at */
  ordering?: "ctrl_no" | "-ctrl_no" | "created_at" | "-created_at";
}

export function useLinkableWorkUnits(
  docId: string | null,
  search: string,
  filters: LinkableWorkUnitFilters = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ["linkable-work-units", docId, search, filters],
    enabled: enabled && !!docId,
    queryFn: () =>
      api
        .get<Paginated<WorkUnitRow>>(
          `/client-docs/${docId}/linkable-work-units`,
          {
            params: {
              search: search || undefined,
              lifecycle:
                filters.lifecycle && filters.lifecycle !== "all"
                  ? filters.lifecycle
                  : undefined,
              clientId: filters.clientId || undefined,
              ordering: filters.ordering || undefined,
              pageSize: 50,
            },
          },
        )
        .then((r) => r.data.results),
  });
}

export function useLinkClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docId: string; workUnitId: string }) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${input.docId}/link`, {
          workUnitId: input.workUnitId,
        })
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useUnlinkClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${docId}/unlink`, {})
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useDeactivateClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docId: string; reason?: string }) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${input.docId}/deactivate`, {
          reason: input.reason || "",
        })
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

export function useActivateClientDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      api
        .post<ClientDocumentRow>(`/client-docs/${docId}/activate`, {})
        .then((r) => r.data),
    onSuccess: () => invalidateAllClientDocLists(qc),
  });
}

// ---------- WorkUnit-side embed ----------

export function useWorkUnitClientDocs(workUnitId: string | null) {
  return useQuery({
    queryKey: ["work-unit-client-docs", workUnitId],
    enabled: !!workUnitId,
    queryFn: () =>
      api
        .get<Paginated<ClientDocumentRow>>(
          `/work-units/${workUnitId}/client-documents`,
        )
        .then((r) => r.data.results),
  });
}
